import {
  Box,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
} from "@chakra-ui/react";
import {
  ColorIcon,
  colors,
  customEvent,
  EventPathType,
  Field,
  getCookie,
  HStack,
  VStack,
} from "goi_common";
import { ReactNode, useCallback, useState } from "react";
import { useRouter } from "next/router";
import { checklistLeadStatus } from "@/apis/postLeadV2";
import { useSubmitLead } from "@/hooks/useSubmitLead";
import { useGetServerTime } from "@/hooks/useGetServerTime";

export default function FileRequestModal({
  isOpen,
  onClose,
  path = "parlor_detail",
  title = "체크리스트 받기",
  description = "장례 비용 예상 견적표, 임종 준비 체크리스트를 받아볼 수 있습니다.",
  isTitleIcon = true,
}: {
  isOpen: boolean;
  onClose: () => void;
  path?: keyof typeof EventPathType;
  title?: string;
  description?: ReactNode;
  isTitleIcon?: boolean;
}) {
  const [type] = useState("번호");
  const [value, setValue] = useState("");
  const [memo, setMemo] = useState("");
  const { data: serverTime } = useGetServerTime();
  const isNight = serverTime?.is_night ?? false;
  // const handleTypeChange = useCallback((type: string) => {
  //   setType(type);
  // }, []);

  const router = useRouter();
  const { submitLead } = useSubmitLead();

  const handleClick = useCallback(() => {
    customEvent({
      newGtm: {
        path,
        trigger_and_target: "file_request_mdl-try_submit",
      },
    });
    if (type === "이메일") {
      alert("현재 이메일 발송 시스템 점검중으로 번호입력만 가능합니다.");
    } else {
      if (!value) {
        alert("번호를 입력해주세요.");
        return;
      } else {
        if (!memo) {
          alert("장례 준비 상황을 알려주세요.");
          return;
        }
        if (/^010-\d{4}-\d{4}$/.test(value)) {
          submitLead({
            phone: value,
            lead_type: "checklist",
            slack_message: `*페이지* ${window.location.origin}${
              router.asPath
            }\n*리퍼러* ${getCookie("referrer") ?? ""}`,
            status: checklistLeadStatus(memo),
            accept_night_call: isNight ? true : undefined,
          })
            .then(() => {
              customEvent({
                newGtm: {
                  path,
                  trigger_and_target: "file_request_mdl-submit_success",
                  text: value,
                },
              });
              alert("신청이 완료되었습니다.");
              onClose();
            })
            .catch(() => {
              alert("번호를 확인해주세요.");
            });
        } else {
          alert("번호 형식을 확인해주세요. (010-****-****)");
          return;
        }
      }
    }
  }, [value, type, memo, isNight, submitLead]);

  return (
    <Box height="100%" width="100%">
      <Modal
        isOpen={isOpen}
        onClose={() => {
          customEvent({
            newGtm: {
              path,
              trigger_and_target: "file_request_mdl-close",
            },
            triggerHackle: true,
          });
          onClose();
        }}
        closeOnOverlayClick={false}
        scrollBehavior="inside"
        isCentered={true}
        useInert={false}
      >
        <ModalOverlay width="100%" height="100%" />
        <ModalContent
          minWidth="300px"
          backgroundColor={colors.white}
          backgroundSize="cover"
        >
          <ModalBody padding="20px 24px 44px">
            <VStack width="100%" gap={10}>
              <HStack width="100%" justifyContent="end" alignItems="center">
                <HStack width="100%" alignItems="center" gap={8}>
                  {isTitleIcon && (
                    <ColorIcon
                      name="list"
                      color={colors.gray900}
                      width={16}
                      height={16}
                    />
                  )}
                  <div className="subtitle_21_b gray900">{title}</div>
                </HStack>
                <div
                  onClick={() => {
                    customEvent({
                      newGtm: {
                        path,
                        trigger_and_target: "file_request_mdl-close",
                      },
                      triggerHackle: true,
                    });
                    onClose();
                  }}
                >
                  <ColorIcon
                    name="close"
                    width={28}
                    height={28}
                    color={colors.black}
                  />
                </div>
              </HStack>
              <VStack width="100%" gap={24}>
                <VStack gap={4} width="100%" className="body_16_m gray800">
                  <Box
                    className="caption_12_b"
                    style={{ whiteSpace: "pre-line", lineHeight: "22px" }}
                  >
                    {description}
                  </Box>
                </VStack>
                {/* <HStack width="100%" gap={12}>
                  <Button
                    width="50%"
                    onClick={() => {
                      customEvent({
                        newGtm: {
                          path: path as any,
                          trigger_and_target: "file_request_mdl-email",
                        },
                      });
                      handleTypeChange("이메일");
                    }}
                    border={`1px solid ${type === "이메일" ? colors.black : "none"}`}
                  >
                    이메일로 받기
                  </Button>
                  <Button
                    width="50%"
                    onClick={() => {
                      customEvent({
                        newGtm: {
                          path: path as any,
                          trigger_and_target: "file_request_mdl-number",
                        },
                      });
                      handleTypeChange("번호");
                    }}
                    border={`1px solid ${type === "번호" ? colors.black : "none"}`}
                  >
                    번호로 받기
                  </Button>
                </HStack> */}
                <Input
                  value={value}
                  onChange={(e) => {
                    const input = e.target.value.replace(/[^0-9]/g, "");
                    let formatted = input;

                    if (input.length <= 3) {
                      formatted = input;
                    } else if (input.length <= 7) {
                      formatted = `${input.slice(0, 3)}-${input.slice(3)}`;
                    } else if (input.length <= 11) {
                      formatted = `${input.slice(0, 3)}-${input.slice(
                        3,
                        7
                      )}-${input.slice(7)}`;
                    }

                    setValue(formatted);
                  }}
                  maxLength={13}
                  type={type === "이메일" ? "email" : "tel"}
                  border={`1px solid ${colors.gray400}`}
                  width="100%"
                  placeholder={
                    type === "이메일" ? "이메일을 입력하세요" : "010-****-****"
                  }
                />
                <Box>
                  <Box className="gray700 subtitle_14_b" pb="12px">
                    상황을 알려주세요(맞춤형 안내)
                  </Box>
                  <Field.Radio
                    title=""
                    items={[
                      "미리 준비하려고 해요",
                      "임종이 임박하신 상태입니다.",
                      "임종하신 상태입니다.",
                    ]}
                    value={memo}
                    onChange={(e) => {
                      customEvent({
                        newGtm: {
                          path,
                          trigger_and_target: "recommendation_memo-option",
                          text: e,
                        },
                      });
                      setMemo(e);
                    }}
                    radioWidth="100%"
                    className="body_14_r"
                    flexDirection="column"
                    alignItem="center"
                  />
                </Box>

                <VStack width="100%" gap={8}>
                  <HStack alignItems="center" justifyContent="space-between">
                    <HStack alignItems="center" gap={4}>
                      <Box className="caption_11_b gray400">필수</Box>
                      <Box className="caption_11_b gray700">
                        개인정보 수집, 이용, 제공 동의
                      </Box>
                    </HStack>
                    <a
                      className="caption_11_b"
                      href="https://goifuneral.notion.site/146a79013e6c40a9b4731dd07a8ad99d"
                      target="_blank"
                    >
                      보기
                    </a>
                  </HStack>
                  {isNight && (
                    <HStack alignItems="center" justifyContent="space-between">
                      <HStack alignItems="center" gap={4}>
                        <Box className="caption_11_b gray400">필수</Box>
                        <Box className="caption_11_b gray700">
                          긴급 상황 시 수신 동의(야간)
                        </Box>
                      </HStack>
                      <a
                        className="caption_11_b"
                        href="https://goifuneral.notion.site/3e893cfdb8584fb28a4086a60aef5ad4"
                        target="_blank"
                      >
                        보기
                      </a>
                    </HStack>
                  )}
                </VStack>
                <Button
                  width="100%"
                  backgroundColor={colors.orange650}
                  color={colors.white}
                  onClick={handleClick}
                >
                  동의하고 자료 받아보기
                </Button>
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
